import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Initial set of languages
const INITIAL_LANGUAGES = [
  { name: 'English', code: 'en-IN', flag: '🇮🇳', selected: true },
  { name: 'Hindi', code: 'hi', flag: '🇮🇳', selected: false },
  { name: 'English (UK)', code: 'en-GB', flag: '🇬🇧', selected: false },
  { name: 'English (US)', code: 'en-US', flag: '🇺🇸', selected: false },
  { name: 'Deutsch', code: 'de', flag: '🇩🇪', selected: false },
  { name: 'Français', code: 'fr', flag: '🇫🇷', selected: false },
  { name: 'Español', code: 'es', flag: '🇪🇸', selected: false },
  { name: 'Italiano', code: 'it', flag: '🇮🇹', selected: false },
  { name: 'Português', code: 'pt', flag: '🇵🇹', selected: false },
  { name: '日本語', code: 'ja', flag: '🇯🇵', selected: false },
  { name: '한국어', code: 'ko', flag: '🇰🇷', selected: false },
  { name: '中文', code: 'zh', flag: '🇨🇳', selected: false },
  { name: 'العربية', code: 'ar', flag: '🇸🇦', selected: false },
  { name: 'Русский', code: 'ru', flag: '🇷🇺', selected: false },
];

const LanguageModal = ({ isOpen, onClose, onLanguageChange }) => {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState(INITIAL_LANGUAGES);
  const [newLanguage, setNewLanguage] = useState({ name: '', code: '', flag: '🌐' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Load languages from localStorage if available
    const savedLanguages = localStorage.getItem('languages');
    if (savedLanguages) {
      setLanguages(JSON.parse(savedLanguages));
    }
  }, []);

  const saveLanguages = (updatedLanguages) => {
    localStorage.setItem('languages', JSON.stringify(updatedLanguages));
    setLanguages(updatedLanguages);
  };

  const handleLanguageSelect = (selectedCode) => {
    const updatedLanguages = languages.map(lang => ({
      ...lang,
      selected: lang.code === selectedCode
    }));
    
    saveLanguages(updatedLanguages);
    
    const selectedLanguage = updatedLanguages.find(lang => lang.selected);
    if (onLanguageChange && selectedLanguage) {
      onLanguageChange(selectedLanguage);
    }
    
    onClose();
  };

  const handleAddLanguage = (e) => {
    e.preventDefault();
    if (newLanguage.name && newLanguage.code) {
      const updatedLanguages = [
        ...languages,
        { ...newLanguage, selected: false }
      ];
      saveLanguages(updatedLanguages);
      setNewLanguage({ name: '', code: '', flag: '🌐' });
      setShowAddForm(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredLanguages = languages.filter(lang => 
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Select your language</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <AddButtonContainer>
          <AddButton onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? '✕' : '+ Add Language'}
          </AddButton>
        </AddButtonContainer>

        {showAddForm && (
          <AddLanguageForm onSubmit={handleAddLanguage}>
            <FormInput
              type="text"
              placeholder="Language name (e.g. Spanish)"
              value={newLanguage.name}
              onChange={(e) => setNewLanguage({...newLanguage, name: e.target.value})}
              required
            />
            <FormInput
              type="text"
              placeholder="Language code (e.g. es)"
              value={newLanguage.code}
              onChange={(e) => setNewLanguage({...newLanguage, code: e.target.value})}
              required
            />
            <FlagSelector>
              <label>Select flag:</label>
              <FlagOptions>
                {['🌐', '🇺🇸', '🇬🇧', '🇮🇳', '🇩🇪', '🇫🇷', '🇪🇸', '🇮🇹', '🇯🇵', '🇰🇷', '🇨🇳', '🇷🇺'].map(flag => (
                  <FlagOption 
                    key={flag}
                    selected={newLanguage.flag === flag}
                    onClick={() => setNewLanguage({...newLanguage, flag})}
                  >
                    {flag}
                  </FlagOption>
                ))}
              </FlagOptions>
            </FlagSelector>
            <SubmitButton type="submit">Add Language</SubmitButton>
          </AddLanguageForm>
        )}

        <LanguageGrid>
          {filteredLanguages.map((language) => (
            <LanguageOption 
              key={language.code}
              selected={language.selected}
              onClick={() => handleLanguageSelect(language.code)}
            >
              <FlagContainer>
                {language.flag}
              </FlagContainer>
              <LanguageName>{language.name}</LanguageName>
              {language.selected && <CheckMark>✓</CheckMark>}
            </LanguageOption>
          ))}
        </LanguageGrid>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  
  @media (max-width: 480px) {
    align-items: flex-start;
    backdrop-filter: none;
  }
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  
  @media (max-width: 480px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    max-height: 100vh;
    padding: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    color: #333;
  }
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const AddButton = styled.button`
  padding: 12px 16px;
  background-color: #0782FE;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background-color: #0669d9;
  }
`;

const AddLanguageForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #f7f9fc;
  padding: 20px;
  margin-bottom: 24px;
  border-radius: 8px;
  border: 1px solid #e0e7ff;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #0782FE;
  }
`;

const FlagSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  label {
    font-weight: 500;
  }
`;

const FlagOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FlagOption = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${props => props.selected ? '#e0e7ff' : '#f0f0f0'};
  border: 2px solid ${props => props.selected ? '#0782FE' : 'transparent'};
  
  &:hover {
    background-color: #e0e7ff;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 16px;
  background-color: #0782FE;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #0669d9;
  }
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${props => props.selected ? '#f0f7ff' : 'transparent'};
  position: relative;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.selected ? '#f0f7ff' : '#f9f9f9'};
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    border-radius: 0;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const FlagContainer = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 12px;
  font-size: 20px;
  background: #f5f5f5;
`;

const LanguageName = styled.span`
  font-size: 16px;
  color: #333;
  flex-grow: 1;
`;

const CheckMark = styled.span`
  color: #0782FE;
  font-size: 18px;
  font-weight: bold;
  
  @media (max-width: 480px) {
    position: absolute;
    right: 12px;
  }
`;

export default LanguageModal; 